import { privateRequest, type ApiResponse } from "../axios";

export interface ComplianceProfileDto {
  Id: number;
  ProfileName: string;
  ProfileCode?: string | undefined;
  Country: string | number;
  StateOrJurisdiction?: string | number | undefined;
  Description?: string | undefined;
  IsActive: boolean;
  CreatedBy?: number | undefined;
  CreateDate?: string | undefined;
  ModifiedBy?: number | undefined;
  ModifiedDate?: string | undefined;
  RuleCount?: number | undefined;
  RulesCount?: number | undefined;
  CountryName?: string | undefined;
  StateName?: string | undefined;
}

export interface SaveComplianceProfileDto {
  Id?: number | undefined;
  CompanyId?: number | undefined;
  ProfileName: string;
  ProfileCode?: string | undefined;
  Country: string | number;
  State?: string | number | undefined;
  StateOrJurisdiction?: string | number | undefined;
  Description?: string | undefined;
  IsActive?: boolean | undefined;
  EffectiveFrom?: string | undefined;
  EffectiveTo?: string | null | undefined;
}

export interface ApplicabilityRuleDto {
  Id?: number | undefined;
  RoleId?: number | null | undefined;
  RoleName?: string | undefined;
  DepartmentId?: number | null | undefined;
  DepartmentName?: string | undefined;
  DesignationId?: number | null | undefined;
  DesignationName?: string | undefined;
  Status?: "Applicable" | "Exempted" | "Conditional";
}

export interface StatutoryRuleDto {
  Id: number;
  ProfileId?: number | undefined;
  ComplianceProfileId?: number | undefined;
  RuleCode: string;
  RuleName: string;
  LeaveTypeId?: number | undefined;
  LeaveTypeName?: string | undefined;
  MinEntitlement?: number | null | undefined;
  MinimumEntitlement?: number | null | undefined;
  MaxEntitlement?: number | null | undefined;
  MaxCarryForwardLimit?: number | null | undefined;
  MaximumCarryForward?: number | null | undefined;
  MaxNoticeDays?: number | null | undefined;
  AllowNegativeBalance?: boolean | undefined;
  IsMandatory: boolean;
  SeverityLevel?: string | undefined;
  ApplicabilityGender?: string | undefined;
  ApplicabilityMinServiceDays?: number | null | undefined;
  MinimumServiceRequirement?: number | null | undefined;
  ApplicabilityEmploymentType?: string | undefined;
  Description?: string | undefined;
  IsActive: boolean;
  Version?: number | undefined;
  ProfileName?: string | undefined;
  ComplianceProfileName?: string | undefined;
  ChangeRemarks?: string | undefined;
  CreatedBy?: number | undefined;
  CreateDate?: string | undefined;
  ModifiedBy?: number | undefined;
  ModifiedDate?: string | undefined;
  // UI extended properties
  RuleCategory?: string | undefined;
  Country?: string | undefined;
  LegalFramework?: string | undefined;
  RuleVersion?: string | undefined;
  LegalSourceId?: number | undefined;
  LegalSourceName?: string | undefined;
  ArticleSection?: string | undefined;
  EntitlementFrequency?: string | undefined;
  AllowEncashment?: boolean | undefined;
  LeaveExpiryApplicable?: boolean | undefined;
  MinValue?: number | null | undefined;
  MaxValue?: number | null | undefined;
  PenaltyDetails?: string | undefined;
  RuleSummary?: string | undefined;
  EffectiveFrom?: string | undefined;
  EffectiveTo?: string | null | undefined;
  ApplicabilityRules?: ApplicabilityRuleDto[] | ApplicabilityRuleDto | null | undefined;
}

export interface SaveStatutoryRuleDto {
  Id?: number | undefined;
  ComplianceProfileId: number;
  CompanyId?: number | undefined;
  RuleVersion: string;
  RuleCode: string;
  RuleName: string;
  EffectiveFrom: string;
  EffectiveTo?: string | null | undefined;
  LeaveTypeId?: number | undefined;
  LegalSourceId?: number | undefined;
  ArticleSection?: string | undefined;
  Description?: string | undefined;
  MinEntitlement: number;
  EntitlementFrequency: string;
  MaxCarryForwardLimit: number;
  AllowNegativeBalance?: boolean | undefined;
  AllowEncashment?: boolean | undefined;
  LeaveExpiryApplicable?: boolean | undefined;
  IsActive?: boolean | undefined;
  ApplicabilityRules?: ApplicabilityRuleDto | ApplicabilityRuleDto[] | null | undefined;
}

export interface LegalSourceDto {
  Id: number;
  CompanyId?: number | undefined;
  Jurisdiction: string;
  ActName: string;
  SourceUrl?: string | undefined;
  DocumentUrl?: string | undefined;
  EffectiveDate?: string | undefined;
  Description?: string | undefined;
  IsActive: boolean;
  CreatedBy?: number | undefined;
  CreateDate?: string | undefined;
  ModifiedBy?: number | undefined;
  ModifiedDate?: string | undefined;
  // UI extended properties
  SourceTitle?: string | undefined;
  AuthorityName?: string | undefined;
  PublicationDate?: string | undefined;
}

export interface SaveLegalSourceDto {
  Id?: number | undefined;
  CompanyId?: number | undefined;
  Jurisdiction: string;
  ActName: string;
  SourceUrl?: string | undefined;
  DocumentUrl?: string | undefined;
  EffectiveDate?: string | undefined;
  Description?: string | undefined;
  IsActive?: boolean | undefined;
  // UI extended properties
  SourceTitle?: string | undefined;
  AuthorityName?: string | undefined;
  PublicationDate?: string | undefined;
}

export interface RuleVersionHistoryDto {
  Id: number;
  RuleId: number;
  ProfileId?: number | undefined;
  CompanyId?: number | undefined;
  Version?: number | undefined;
  VersionNumber?: number | undefined;
  RuleVersion?: string | undefined;
  RuleCode?: string | undefined;
  RuleName: string;
  ComplianceProfileName?: string | undefined;
  LeaveTypeId?: number | null | undefined;
  LeaveTypeName?: string | undefined;
  EffectiveFrom?: string | undefined;
  EffectiveTo?: string | null | undefined;
  IsActive?: boolean | undefined;
  MinEntitlement?: number | undefined;
  MinimumEntitlement?: number | null | undefined;
  MaxEntitlement?: number | undefined;
  MaxCarryForwardLimit?: number | undefined;
  MaximumCarryForward?: number | null | undefined;
  MaxNoticeDays?: number | undefined;
  EntitlementFrequency?: string | undefined;
  MinimumServiceRequirement?: number | null | undefined;
  AllowNegativeBalance?: boolean | undefined;
  AllowEncashment?: boolean | undefined;
  IsMandatory?: boolean | undefined;
  SeverityLevel?: string | undefined;
  ApplicabilityGender?: string | undefined;
  ApplicabilityMinServiceDays?: number | undefined;
  ApplicabilityEmploymentType?: string | undefined;
  ApplicabilityRules?: ApplicabilityRuleDto[] | ApplicabilityRuleDto | string | null | undefined;
  LegalSourceName?: string | undefined;
  ArticleSection?: string | undefined;
  Description?: string | undefined;
  ChangeRemarks?: string | undefined;
  ChangeReason?: string | undefined;
  ChangedFields?: string | undefined;
  OldValue?: string | undefined;
  NewValue?: string | undefined;
  CreatedBy?: number | undefined;
  CreatedByName?: string | undefined;
  CreateDate?: string | undefined;
  ModifiedBy?: string | undefined;
  ModifiedDate?: string | undefined;
}

export interface ComplianceValidationItemDto {
  RuleName: string;
  LeaveTypeId?: number | null | undefined;
  LeaveTypeName?: string | undefined;
  Status: "COMPLIANT" | "WARNING" | "NON_COMPLIANT";
  Severity: "ERROR" | "WARNING";
  Message: string;
  ConfiguredValue?: string | undefined;
  ExpectedRange?: string | undefined;
}

export interface PolicyComplianceCheckResultDto {
  OverallStatus: "COMPLIANT" | "WARNING" | "NON_COMPLIANT";
  TotalRulesChecked: number;
  PassedCount: number;
  WarningCount: number;
  NonCompliantCount: number;
  SummaryMessage: string;
  ValidationItems: ComplianceValidationItemDto[];
}

export interface LeavePolicyComplianceValidationDto {
  IsCompliant?: boolean | undefined;
  ComplianceProfileName?: string | undefined;
  Violations?: {
    RuleName: string;
    RuleCategory: string;
    Message: string;
    Severity: "Error" | "Warning";
  }[] | undefined;
  // Backend direct response format
  OverallStatus?: "COMPLIANT" | "WARNING" | "NON_COMPLIANT" | undefined;
  TotalRulesChecked?: number | undefined;
  PassedCount?: number | undefined;
  WarningCount?: number | undefined;
  NonCompliantCount?: number | undefined;
  SummaryMessage?: string | undefined;
  ValidationItems?: ComplianceValidationItemDto[] | undefined;
}

const ENDPOINTS = {
  COMPLIANCE_PROFILE: "api/Compliance/Profile",
  STATUTORY_RULE: "api/Compliance/StatutoryRule",
  LEGAL_SOURCE: "api/Compliance/LegalSource",
  VERSION_HISTORY: "api/Compliance/RuleVersionHistory",
  VALIDATE_POLICY: "api/Compliance/ValidatePolicy",
};

// API Services without hardcoded mock data
const getComplianceProfiles = async (): Promise<ApiResponse<ComplianceProfileDto[]>> => {
  const res = await privateRequest.get<ApiResponse<ComplianceProfileDto[]>>(ENDPOINTS.COMPLIANCE_PROFILE);
  return res.data;
};

const createComplianceProfile = async (payload: SaveComplianceProfileDto): Promise<ApiResponse<any>> => {
  const res = await privateRequest.post<ApiResponse<any>>(ENDPOINTS.COMPLIANCE_PROFILE, payload);
  return res.data;
};

const updateComplianceProfile = async (payload: SaveComplianceProfileDto): Promise<ApiResponse<any>> => {
  const res = await privateRequest.put<ApiResponse<any>>(ENDPOINTS.COMPLIANCE_PROFILE, payload);
  return res.data;
};

const toggleComplianceProfileStatus = async (id: number): Promise<ApiResponse<any>> => {
  const res = await privateRequest.post<ApiResponse<any>>(`${ENDPOINTS.COMPLIANCE_PROFILE}/${id}/Toggle`);
  return res.data;
};

const getStatutoryRules = async (profileId?: number): Promise<ApiResponse<StatutoryRuleDto[]>> => {
  const res = await privateRequest.get<ApiResponse<StatutoryRuleDto[]>>(ENDPOINTS.STATUTORY_RULE, {
    params: profileId ? { profileId } : {},
  });
  return res.data;
};

const createStatutoryRule = async (payload: SaveStatutoryRuleDto): Promise<ApiResponse<any>> => {
  const res = await privateRequest.post<ApiResponse<any>>(ENDPOINTS.STATUTORY_RULE, payload);
  return res.data;
};

const updateStatutoryRule = async (payload: SaveStatutoryRuleDto): Promise<ApiResponse<any>> => {
  const res = await privateRequest.put<ApiResponse<any>>(ENDPOINTS.STATUTORY_RULE, payload);
  return res.data;
};

const deleteStatutoryRule = async (id: number): Promise<ApiResponse<any>> => {
  const res = await privateRequest.delete<ApiResponse<any>>(`${ENDPOINTS.STATUTORY_RULE}/${id}`);
  return res.data;
};

const getLegalSources = async (): Promise<ApiResponse<LegalSourceDto[]>> => {
  const res = await privateRequest.get<ApiResponse<LegalSourceDto[]>>(ENDPOINTS.LEGAL_SOURCE);
  return res.data;
};

const createLegalSource = async (payload: SaveLegalSourceDto): Promise<ApiResponse<any>> => {
  const res = await privateRequest.post<ApiResponse<any>>(ENDPOINTS.LEGAL_SOURCE, payload);
  return res.data;
};

const updateLegalSource = async (payload: SaveLegalSourceDto): Promise<ApiResponse<any>> => {
  const res = await privateRequest.put<ApiResponse<any>>(ENDPOINTS.LEGAL_SOURCE, payload);
  return res.data;
};

const deleteLegalSource = async (id: number): Promise<ApiResponse<any>> => {
  const res = await privateRequest.delete<ApiResponse<any>>(`${ENDPOINTS.LEGAL_SOURCE}/${id}`);
  return res.data;
};

const getRuleVersionHistory = async (ruleId?: number): Promise<ApiResponse<RuleVersionHistoryDto[]>> => {
  const res = await privateRequest.get<ApiResponse<RuleVersionHistoryDto[]>>(ENDPOINTS.VERSION_HISTORY, {
    params: ruleId ? { ruleId } : {},
  });
  return res.data;
};

const validateLeavePolicyCompliance = async (policyPayload: any): Promise<LeavePolicyComplianceValidationDto> => {
  try {
    const res = await privateRequest.post<ApiResponse<LeavePolicyComplianceValidationDto>>(ENDPOINTS.VALIDATE_POLICY, policyPayload);
    if (res?.data?.data) return res.data.data;
  } catch (e) {
    // Graceful error handling
  }

  return {
    IsCompliant: true,
    ComplianceProfileName: "Standard",
    Violations: [],
  };
};

const LeaveComplianceService = {
  getComplianceProfiles,
  createComplianceProfile,
  updateComplianceProfile,
  toggleComplianceProfileStatus,
  getStatutoryRules,
  createStatutoryRule,
  updateStatutoryRule,
  deleteStatutoryRule,
  getLegalSources,
  createLegalSource,
  updateLegalSource,
  deleteLegalSource,
  getRuleVersionHistory,
  validateLeavePolicyCompliance,
};

export default LeaveComplianceService;
